import { useEffect, useMemo, useState } from "react"
import { generate } from "library/guid"
import { errorSnackbar } from "lib/snackbar/error-snackbar"
import { warningSnackbar } from "lib/snackbar/warning-snackbar"
import { ToBeSaved } from "lib/@components/save-indicator/to-be-saved"
import { assignSchedulesToTrade } from "routes/skill-mapper/controller/assign-schedules-to-trade"
import { removeScheduleAssignments } from "routes/skill-mapper/controller/remove-schedule-assignments"
import {
    currentWriteKeys,
    isPendingAssignmentId,
    mergeOptimisticAssignments,
    operationTargetKeys,
    pendingAssignment,
    withoutSettledOperations,
} from "./merge-optimistic-assignments"

// A saved change the server's list never shows (the target was changed again from another
// tab, say) would otherwise stay on the board for good; after this long the list wins
export const SAVED_CHANGE_TIMEOUT_MS = 10000

export const CHANGED_SINCE_READ =
    "This assignment changed after the board loaded it, so it wasn't removed. The board now shows the latest."

/** The page's save indicator for board saves: <SaveIndicator id={ASSIGNMENT_SAVE_ID} /> */
export const ASSIGNMENT_SAVE_ID = "scheduleAssignments"

// Board saves still on their way, for the save indicator. Module level for the same reason
// as savesInFlight: a save outlives the board that started it
let savesPending = 0

// Skill map and target key to the latest save sent or queued for it. Kept at module level,
// not in the hook, so the order still holds when the board remounts while a save is on its
// way: choosing another regime remounts it, and switching to the skill routing view
// unmounts the whole panel.
const savesInFlight = new Map()

/**
 * Assign and remove without blocking the page (FIQ-2457). Each change shows immediately;
 * once its save has finished it is dropped when the server's list shows it (see
 * withoutSettledOperations). A failed save is rolled back with an error message.
 *
 * Saves for the same target are sent one at a time, in the order they were made. Sent
 * together, the server could process a Gas assign after the Electrical move made straight
 * after it, and the older choice would end up saved. Saves for other targets are not held up.
 * The order holds across remounts of the board (see savesInFlight).
 *
 * @param {{ skillMapId: string, assignments: object[] | undefined }} options
 * @returns {{ assignments: object[], assign: Function, remove: Function }}
 */
export function useOptimisticAssignments({ skillMapId, assignments }) {
    const [operations, setOperations] = useState([])
    // Also on operations: a save can return after the list that shows it has arrived
    useEffect(dropSettledOperations, [assignments, operations])
    const merged = useMemo(merge, [assignments, operations])

    return { assignments: merged, assign, remove }

    function merge() {
        return mergeOptimisticAssignments(assignments ?? [], operations)
    }

    // Nothing is settled against a list that has not loaded yet
    function dropSettledOperations() {
        if (assignments) setOperations((current) => withoutSettledOperations(assignments, current))
    }

    function assign(tradeId, targets) {
        const records = targets.map(toPendingRecord)
        // The writes this assign replaces, so a list still showing them reads as stale
        const replaces = currentWriteKeys(assignments ?? [], targets)
        return run({ type: "assign", records, replaces }, () =>
            assignSchedulesToTrade({ skillMapId, tradeId, targets })
        )

        function toPendingRecord(target) {
            return pendingAssignment(target, tradeId)
        }
    }

    // Only real, saved records can be removed; a stand-in's id means nothing to the server.
    // Each goes with the writeId it was read with, so the server keeps a record that was
    // re-assigned while this remove was still on its way. The removed records also travel as
    // targets, so the merge can tell this remove supersedes an earlier assign still saving.
    function remove(ids) {
        const savedIds = ids.filter(isSavedId)
        if (!savedIds.length) return Promise.resolve(false)
        const refs = savedIds.map(toRef)
        const targets = savedIds.map(findSaved).filter(Boolean)
        return run({ type: "remove", refs, targets }, () =>
            removeScheduleAssignments({ skillMapId, assignments: refs })
        )
    }

    function toRef(id) {
        return { id, writeId: findSaved(id)?.writeId ?? null }
    }

    function findSaved(id) {
        return (assignments ?? []).find(hasId)

        function hasId(assignment) {
            return assignment.id === id
        }
    }

    async function run(operation, save) {
        const id = generate()
        setOperations((current) => [...current, { ...operation, id, saved: false }])
        reportSaveStarted()
        try {
            const result = await afterEarlierSaves(operationTargetKeys(operation), save)
            reportSaveFinished(null)
            // The server keeps a record written since this board read it (another tab, or a
            // save queued ahead of this remove). Say so, and let the list show that record
            if (operation.type === "remove" && result === false) {
                setOperations((current) => current.filter(isOther))
                warningSnackbar(CHANGED_SINCE_READ)
                return false
            }
            setOperations((current) => current.map(markSaved))
            setTimeout(dropIfStillHere, SAVED_CHANGE_TIMEOUT_MS)
            return true
        } catch (error) {
            reportSaveFinished(error)
            setOperations((current) => current.filter(isOther))
            errorSnackbar(describeFailure(operation, error))
            return false
        }

        // Stand-ins stay pending (faded, not removable) until the server's list shows the
        // change; the merge then swaps in the server's own record and the operation is dropped
        function markSaved(op) {
            return op.id === id ? { ...op, saved: true } : op
        }

        function dropIfStillHere() {
            setOperations((current) => (current.some(isThis) ? current.filter(isOther) : current))
        }

        function isThis(op) {
            return op.id === id
        }

        function isOther(op) {
            return op.id !== id
        }
    }

    // Wait for every earlier save on these targets to finish, succeed or fail, then send
    // this one. A failed earlier save does not stop it: this is the user's latest choice.
    function afterEarlierSaves(targetKeys, save) {
        const keys = targetKeys.map(toQueueKey)
        const earlier = keys.map(inFlightFor).filter(Boolean)
        const saving = Promise.allSettled(earlier).then(save)
        for (const key of keys) savesInFlight.set(key, saving)
        saving.then(forget, forget)
        return saving

        function forget() {
            for (const key of keys) {
                if (savesInFlight.get(key) === saving) savesInFlight.delete(key)
            }
        }
    }

    function toQueueKey(targetKey) {
        return `${skillMapId}|${targetKey}`
    }
}

// Pending while any board save is on its way; then Saved, or Error if one failed (until the
// next save starts)
function reportSaveStarted() {
    savesPending += 1
    ToBeSaved(ASSIGNMENT_SAVE_ID).raise(true)
}

function reportSaveFinished(error) {
    savesPending = Math.max(0, savesPending - 1)
    if (error) ToBeSaved(ASSIGNMENT_SAVE_ID).raise(error instanceof Error ? error : new Error(String(error)))
    else if (!savesPending) ToBeSaved(ASSIGNMENT_SAVE_ID).raise(false)
}

function inFlightFor(key) {
    return savesInFlight.get(key)
}

// A save of several targets is written in batches, not all or nothing, so some of it may
// have landed; the list refreshes on those writes and the board shows them
function describeFailure(operation, error) {
    if (operationTargetKeys(operation).length > 1) {
        return `Couldn't save all of these changes. Some may have saved, and the board shows what did: ${error.message}`
    }
    return `Couldn't save the assignment: ${error.message}`
}

function isSavedId(id) {
    return !isPendingAssignmentId(id)
}
